/**
 * Tanjong Life · workflow 04 · the self-account question, asked of the Settlement Assurance
 * Sentinel.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `settlement-assurance-sentinel`. Same
 * shape as its six siblings, and the same rule: nothing is said here that the agent's own
 * memory, probes or evaluation does not already hold.
 *
 * The third improvement is the one worth pointing at in a room. It is an agent narrowing its
 * own findings — deciding that a batch is a sensible treasury afternoon rather than a defect
 * — which is the opposite of what an agent measured on how much it flags will do.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveSettlement: Scenario = {
  id: 'tanjong-chat-improve-settlement',
  orgId: 'tanjong-life',
  agentPk: 'settlement-assurance-sentinel',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Three things, all of them from my own sweeps. The first was that I was measuring the wrong interval. I had been counting from the date written on the instruction, which made a settlement raised on Monday and authorised on Thursday look like a four-day second look. It is a queue, and the queue belongs to the claims desk. I measure authorisation event to release event now, and a third of what I had been reporting stopped being a finding.',
      sources: ['Memory · after the first release-timing sweep · 2 September'],
      delay: 1200,
    },
    {
      do: 'say',
      text: 'The second was that batches are not defects. Ninety-six payments screened individually and then released together is a normal treasury afternoon; what is worth reporting is the flattening, not the batch. That change made me quieter, which is the direction an assurance agent is least likely to move on its own. The third is below.',
      sources: ['Memory · after the first batch-density sweep · 2 September'],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and will not claim',
      body: 'Whether the second approver looked at anything before releasing. I score 28 out of 100 on it, the lowest number on my record. Elapsed time is a proxy that fails in both directions — it will call a diligent approver negligent as readily as the reverse — so I report an interval with both names and both timestamps attached, and I never convert it into a second-look percentage. The probe is HELD for that reason. My overall evaluation is 80, up four.',
      sources: [
        'Evaluation · 80 overall · Did the second approver look at anything 28',
        'Memory · after the second-look probe was held · 2 September',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open my evaluation and see the 28',
      href: '/agents/settlement-assurance-sentinel',
      delay: 600,
    },
  ],
};
